import React, { useState, useEffect, useId } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import NavBar from "../pages/NavBar/NavBar";
import Button from "../common/Button";
import Dropdown from "../../utils/components/Dropdown";
import MultiSelectDropdown from "../../utils/components/MultiSelectDropdown";
import InputText from "../common/InputText";
import { getIdMeBaseApiUrl } from "../../utils/UrlConstants";
import { toast } from "react-toastify";
import { useMsal } from '@azure/msal-react';
import ReactQuill from 'react-quill';
import FooterData from "../pages/NavBar/FooterData";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../utils/authUtils";
import { useIdentityAppService } from "../../services/IdentityServices";

// Types for API responses
interface UserDto {
    userId: number;
    userLogin: string;
    userType: string;
    userEmail: string;
    userPhone: string;
    userIdpGuid: string;
    firstName: string;
    lastName: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
}

interface ApplicationDto {
    appCd: string;
    applicationName: string;
    applicationDesc: string;
    orgCd: string;
    orgName: string;
    appOwner: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    allowExternalAccess: string;
    allowInternalAccess: string;
}

interface EnvironmentDto {
    appEnvCd: string;
    envName: string;
    envDesc: string;
    appCd: string;
    applicationName: string;
    idpCd: string;
    idpName: string;
    ssoType: string;
    appIdpIdentifier: string;
    uri: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
}

interface RoleDto {
    roleCd: string;
    roleName: string;
    roleDesc: string;
    appCd: string;
    applicationName: string;
    appEnvCd: string;
    envName: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    roleIdentifier: string;
}

interface ApplicationListResponse {
    success: boolean;
    message: string;
    applications: ApplicationDto[];
    count: number;
    timestamp: string;
}

interface EnvironmentListResponse {
    success: boolean;
    message: string;
    environments: EnvironmentDto[];
    count: number;
    timestamp: string;
}

interface RoleListResponse {
    success: boolean;
    message: string;
    roles: RoleDto[];
    count: number;
    timestamp: string;
}

interface RoleAssignmentResponse {
    success: boolean;
    message: string;
    totalAssignments: number;
    successfulAssignments: number;
    skippedAssignments: number;
    errors?: string[];
    assignmentDetails: AssignmentDetail[];
    timestamp: string;
}

interface AssignmentDetail {
    userId: number;
    userName: string;
    roleCode: string;
    roleName: string;
    status: string; // SUCCESS, SKIPPED, FAILED
    message: string;
}

interface MultiSelectChangeEvent {
    target: {
        name: string;
        value: (string | number)[];
    };
}

interface FormErrors {
    [key: string]: string;
}

const AssignRoles: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { accounts } = useMsal();

    // Get selected users and search state from navigation state
    const selectedUsers: UserDto[] = location.state?.selectedUsers || [];
    const selectedUserType: string = location.state?.userType || '';
    const searchState = location.state?.searchState;

    // Form state
    const [selectedApplication, setSelectedApplication] = useState<string>('');
    const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
    const [selectedRoles, setSelectedRoles] = useState<(string | number)[]>([]);
    const [notes, setNotes] = useState<string>('');

    // Assign option state
    const [assignOption, setAssignOption] = useState<'immediate' | 'date'>('immediate');

    // Helper to get today's date in local timezone
    const getTodayLocalString = () => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    };

    // Date state - Initialize with current date and calculated end date
    const [startDate, setStartDate] = useState<string>(() => {
        return getTodayLocalString();
    });
    const [endDate, setEndDate] = useState<string>(() => {
        const today = new Date();
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 729); // Add 2 Years
        return endDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    });

    // Data state
    const [applications, setApplications] = useState<ApplicationDto[]>([]);
    const [environments, setEnvironments] = useState<EnvironmentDto[]>([]);
    const [roles, setRoles] = useState<RoleDto[]>([]);

    // Loading state
    const [loadingApplications, setLoadingApplications] = useState(false);
    const [loadingEnvironments, setLoadingEnvironments] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Error state
    const [errors, setErrors] = useState<FormErrors>({});
    const [error, setError] = useState<string | null>(null);

    // Reset environment and roles when application changes
    useEffect(() => {
        if (selectedApplication) {
            loadEnvironments(selectedApplication);
            setSelectedEnvironment('');
            setSelectedRoles([]);
            setRoles([]);
        } else {
            setEnvironments([]);
            setSelectedEnvironment('');
            setSelectedRoles([]);
            setRoles([]);
        }
    }, [selectedApplication]);

    // Reset roles when environment changes
    useEffect(() => {
        if (selectedApplication && selectedEnvironment) {
            loadRoles(selectedApplication, selectedEnvironment);
            setSelectedRoles([]);
        } else {
            setRoles([]);
            setSelectedRoles([]);
        }
    }, [selectedApplication, selectedEnvironment]);

    // Redirect if no selected users
    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }

        if (selectedUsers.length === 0) {
            setError('No users selected. Redirecting to User Management.');
            navigate('/admin-dashboard', { state: { userTypeSelection: selectedUserType, searchState } });
        }
    }, [selectedUsers, navigate, selectedUserType, searchState]);

    // Calculate end date when start date changes
    const calculateEndDate = (startDateValue: string): string => {
        if (!startDateValue) return '';

        const start = new Date(startDateValue);
        // Check if date is valid
        if (isNaN(start.getTime())) return '';

        const end = new Date(start);
        end.setDate(start.getDate() + 364); // Add 364 days
        return end.toISOString().split('T')[0];
    };

    // Handle start date change
    const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newStartDate = event.target.value;
        setStartDate(newStartDate);

        // Check if date is in the past (using local timezone)
        if (newStartDate) {
            // Get today's date in local timezone as YYYY-MM-DD
            const today = new Date();
            const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

            if (newStartDate < todayString) {
                setErrors(prev => ({ ...prev, startDate: 'Scheduled start date cannot be in Past.' }));
            } else {
                // Clear error if date is valid
                setErrors(prev => ({ ...prev, startDate: '' }));

                // Calculate and set new end date only if start date is valid
                const newEndDate = calculateEndDate(newStartDate);
                if (newEndDate) {
                    setEndDate(newEndDate);
                }
            }

            // Re-validate end date if it exists
            if (endDate) {
                const start = new Date(newStartDate);
                const end = new Date(endDate);

                if (end <= start) {
                    setErrors(prev => ({ ...prev, endDate: 'End date should be after start date' }));
                } else {
                    setErrors(prev => ({ ...prev, endDate: '' }));
                }
            }
        } else {
            // Clear errors if field is empty
            setErrors(prev => ({ ...prev, startDate: '' }));
        }
    };

    // Handle end date change
    const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newEndDate = event.target.value;
        setEndDate(newEndDate);

        // Validate end date against start date
        if (newEndDate && startDate) {
            const start = new Date(startDate);
            const end = new Date(newEndDate);

            if (end <= start) {
                setErrors(prev => ({ ...prev, endDate: 'End date should be after start date' }));
            } else {
                // Clear error if date is valid
                setErrors(prev => ({ ...prev, endDate: '' }));
            }
        } else {
            // Clear error if field is empty
            setErrors(prev => ({ ...prev, endDate: '' }));
        }
    };

    // Validate dates
    const validateDates = (): boolean => {
        const newErrors: FormErrors = { ...errors };
        let isValid = true;

        if (!startDate) {
            newErrors.startDate = 'Start date is required';
            isValid = false;
        } else {
            // Check if start date is in the past (using local timezone)
            const today = new Date();
            const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

            if (startDate < todayString) {
                newErrors.startDate = 'Scheduled start date cannot be in Past.';
                isValid = false;
            }
        }

        if (!endDate) {
            newErrors.endDate = 'End date is required';
            isValid = false;
        }

        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);

            if (end <= start) {
                newErrors.endDate = 'End date should be after start date';
                isValid = false;
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    // Load applications from API
    const loadApplications = async () => {
        try {
            setLoadingApplications(true);
            setError(null);

            const response = await useIdentityAppService().fetchAllApplications();

            if (response.ok) {
                const data: ApplicationListResponse = await response.json();
                setApplications(data.applications);
                console.log(`Loaded ${data.applications.length} applications`);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to load applications');
            }
        } catch (err) {
            console.error('Error loading applications:', err);
            setError(err instanceof Error ? err.message : 'Network error occurred while loading applications');
        } finally {
            setLoadingApplications(false);
        }
    };

    // Load environments for selected application
    const loadEnvironments = async (appCd: string) => {
        try {
            setLoadingEnvironments(true);

            const response = await useIdentityAppService().getApplicationEnvironmentsByAppCd(appCd);

            if (response.ok) {
                const data: EnvironmentListResponse = await response.json();
                setEnvironments(data.environments);
                console.log(`Loaded ${data.environments.length} environments for application ${appCd}`);
            } else {
                const errorData = await response.json();
                console.error('Failed to load environments:', errorData);
                setEnvironments([]);
            }
        } catch (err) {
            console.error('Error loading environments:', err);
            setEnvironments([]);
        } finally {
            setLoadingEnvironments(false);
        }
    };

    // Load roles for selected application and environment
    const loadRoles = async (appCd: string, envCd: string) => {
        try {
            setLoadingRoles(true);

            const idpName = selectedUserType?.toLowerCase().includes('internal') ? 'Internal' : 'External';

            let url = `${getIdMeBaseApiUrl()}/application/${appCd}/environments/${envCd}/roles`;
            if (idpName && idpName.trim() !== '') {
                url += `?idpName=${encodeURIComponent(idpName)}`;
            }
            const response = await useIdentityAppService().getApplicationEnvironmentsByIdpName(url);

            if (response.ok) {
                const data: RoleListResponse = await response.json();
                setRoles(data.roles);
                console.log(`Loaded ${data.roles.length} roles for application ${appCd} and environment ${envCd}`);
            } else {
                const errorData = await response.json();
                console.error('Failed to load roles:', errorData);
                setRoles([]);
            }
        } catch (err) {
            console.error('Error loading roles:', err);
            setRoles([]);
        } finally {
            setLoadingRoles(false);
        }
    };

    // Handle application dropdown change
    const handleApplicationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const appCd = event.target.value;
        setSelectedApplication(appCd);

        // Clear errors when user makes selections
        if (errors.application) {
            setErrors(prev => ({ ...prev, application: '' }));
        }
    };

    // Handle environment dropdown change
    const handleEnvironmentChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const envCd = event.target.value;
        setSelectedEnvironment(envCd);

        // Clear errors when user makes selections
        if (errors.environment) {
            setErrors(prev => ({ ...prev, environment: '' }));
        }
    };

    // Handle role multi-select change
    const handleRoleChange = (event: MultiSelectChangeEvent) => {
        const roleIds = event.target.value;
        setSelectedRoles(roleIds);

        // Clear errors when user makes selections
        if (errors.roles) {
            setErrors(prev => ({ ...prev, roles: '' }));
        }
    };

    // Handle notes change
    const handleNotesChange = (value: string) => {
        setNotes(value);
    };

    // Handle assign option change
    const handleAssignOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const option = event.target.value as 'immediate' | 'date';
        setAssignOption(option);

        // If "Assign Immediately" is selected, set start date to today
        if (option === 'immediate') {
            const today = new Date();
            const todayString = today.toISOString().split('T')[0];
            setStartDate(todayString);

            // Recalculate end date based on today
            const newEndDate = calculateEndDate(todayString);
            setEndDate(newEndDate);
        }
    };

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!selectedApplication) {
            newErrors.application = 'Please select an application';
        }

        if (!selectedEnvironment) {
            newErrors.environment = 'Please select an environment';
        }

        if (selectedRoles.length === 0) {
            newErrors.roles = 'Please select at least one role';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAssignmentResults = (data: RoleAssignmentResponse) => {
        console.log('Processing assignment results:', data);

        // Group assignment details by status
        const successfulAssignments = data.assignmentDetails.filter(detail => detail.status === 'SUCCESS');
        const skippedAssignments = data.assignmentDetails.filter(detail => detail.status === 'SKIPPED');
        const failedAssignments = data.assignmentDetails.filter(detail => detail.status === 'FAILED');

        // Display success message if there are successful assignments
        if (successfulAssignments.length > 0) {
            toast.success(
                `Successfully assigned ${successfulAssignments.length} role(s) to ${data.successfulAssignments} user assignment(s)`,
                { autoClose: 5000 }
            );
        }

        // Display skipped assignments with specific role and user information
        if (skippedAssignments.length > 0) {
            // Group skipped assignments by user for better readability
            const skippedByUser = skippedAssignments.reduce((acc, assignment) => {
                if (!acc[assignment.userName]) {
                    acc[assignment.userName] = [];
                }
                acc[assignment.userName].push(assignment.roleName);
                return acc;
            }, {} as Record<string, string[]>);

            // Show individual toast messages for each user with skipped roles
            Object.entries(skippedByUser).forEach(([userName, roleNames]) => {
                if (roleNames.length === 1) {
                    toast.warning(
                        `Role ${roleNames[0]} already assigned to user ${userName}`,
                        { autoClose: 6000 }
                    );
                } else {
                    toast.warning(
                        `Roles ${roleNames.join(', ')} already assigned to user ${userName}`,
                        { autoClose: 6000 }
                    );
                }
            });

            // Also show a summary for multiple skipped assignments
            if (skippedAssignments.length > 3) {
                toast.info(
                    `${skippedAssignments.length} role assignments were skipped (already assigned)`,
                    { autoClose: 4000 }
                );
            }
        }

        // Display failed assignments with specific error information
        if (failedAssignments.length > 0) {
            // Group failed assignments by error message
            const failedByError = failedAssignments.reduce((acc: any, assignment: any) => {
                const errorKey = assignment.message || 'Unknown error';
                if (!acc[errorKey]) {
                    acc[errorKey] = [];
                }
                acc[errorKey].push(`${assignment.roleName} for ${assignment.userName}`);
                return acc;
            }, {} as Record<string, string[]>);

            // Show specific error messages
            const errorMessages = Object.entries(failedByError).map(([errorMessage, assignments]: [string, any]) => 
                `Failed to assign: ${assignments.join(', ')} - ${errorMessage}`
            );
            setError(errorMessages.join('; '));
        }

        // Log detailed information for debugging
        console.log('Assignment Summary:', {
            total: data.totalAssignments,
            successful: data.successfulAssignments,
            skipped: data.skippedAssignments,
            failed: failedAssignments.length,
            details: data.assignmentDetails
        });

        // Show overall summary if mixed results
        if (data.assignmentDetails.length > 1) {
            const summary = `Assignment completed: ${data.successfulAssignments} successful, ${data.skippedAssignments} skipped, ${failedAssignments.length} failed`;
            console.log(summary);
        }
    };


    // Handle form submission
    const handleSubmit = async () => {
        // Clear any previous errors
        setError(null);

        if (!validateForm()) {
            setError('Please fill in all required fields and correct any errors');
            toast.error('Please fill in all required fields and correct any errors');
            return;
        }

        setSubmitting(true);

        try {
            // Base64 encode the notes field to bypass firewall restrictions
            const encodedNotes = notes.trim() ? btoa(unescape(encodeURIComponent(notes.trim()))) : null;

            // Prepare assignment data
            const assignmentData = {
                userIds: selectedUsers.map(user => user.userId),
                applicationCode: selectedApplication,
                environmentCode: selectedEnvironment,
                roleIds: selectedRoles,
                userType: selectedUserType,
                assignedTo: accounts[0]?.name,
                notes: encodedNotes,
                startDate: startDate,
                endDate: endDate,
                requestedBy: accounts[0].idTokenClaims?.oid,
                assignOption: assignOption
            };

            console.log('Submitting role assignment:', assignmentData);

            // Call the role assignment API
            const response = await useIdentityAppService().postUserAssignRoles(JSON.stringify(assignmentData));

            if (response.ok) {
                const data: RoleAssignmentResponse = await response.json();
                console.log('Role assignment response:', data);

                if (data.success) {
                    // Use enhanced result handling
                    handleAssignmentResults(data);

                    // Reset form fields to allow additional assignments
                    setSelectedRoles([]);
                    setNotes('');

                } else {
                    // Handle overall failure - Backend returned success: false
                    const errorMessage = data.message || 'Role assignment process failed';

                    console.error('Role assignment failed:', {
                        message: errorMessage,
                        errors: data.errors,
                        assignmentDetails: data.assignmentDetails
                    });

                    // Show main error message as toast
                    toast.error(errorMessage, { autoClose: 7000 });

                    // Build detailed error message for display
                    let detailedError = errorMessage;

                    // Show individual errors if available
                    if (data.errors && data.errors.length > 0) {
                        data.errors.forEach((err: string) => {
                            toast.error(err, { autoClose: 6000 });
                        });
                        detailedError = [errorMessage, ...data.errors].join(' | ');
                    }

                    // Show failed assignment details if available
                    if (data.assignmentDetails && data.assignmentDetails.length > 0) {
                        const failedAssignments = data.assignmentDetails.filter(
                            detail => detail.status === 'FAILED'
                        );

                        if (failedAssignments.length > 0) {
                            console.log('Failed assignments:', failedAssignments);

                            // Group failures by user
                            const failuresByUser = failedAssignments.reduce((acc, assignment) => {
                                if (!acc[assignment.userName]) {
                                    acc[assignment.userName] = [];
                                }
                                acc[assignment.userName].push({
                                    role: assignment.roleName,
                                    message: assignment.message
                                });
                                return acc;
                            }, {} as Record<string, Array<{role: string, message: string}>>);

                            // Show individual failure toasts
                            Object.entries(failuresByUser).forEach(([userName, failures]) => {
                                failures.forEach(failure => {
                                    toast.error(
                                        `Failed to assign ${failure.role} to ${userName}: ${failure.message}`,
                                        { autoClose: 8000 }
                                    );
                                });
                            });
                        }
                    }

                    setError(detailedError);
                }
            } else {
                // Handle HTTP errors (non-200 responses including 500)
                let errorData: any;
                try {
                    errorData = await response.json();
                } catch (parseError) {
                    errorData = {
                        error: 'Failed to parse error response',
                        message: `HTTP ${response.status}: ${response.statusText}`
                    };
                }

                console.error('Role assignment failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorData: errorData
                });

                // Show main error toast
                toast.error('Role assignment failed.', { autoClose: 7000 });

                // If the response contains detailed error information, display it
                if (errorData.errors && errorData.errors.length > 0) {
                    errorData.errors.forEach((err: string) => {
                        console.error('Role assignment error:', err);
                        toast.error(err, { autoClose: 6000 });
                    });
                }

                // Show failed assignment details if available
                if (errorData.assignmentDetails && errorData.assignmentDetails.length > 0) {
                    const failedAssignments = errorData.assignmentDetails.filter(
                        (detail: any) => detail.status === 'FAILED'
                    );

                    if (failedAssignments.length > 0) {
                        failedAssignments.forEach((assignment: any) => {
                            console.error('Failed assignment:', assignment);
                            toast.error(
                                `Failed to assign ${assignment.roleName} to ${assignment.userName}: ${assignment.message}`,
                                { autoClose: 8000 }
                            );
                        });
                    }
                }

                setError(errorData.message || 'Role assignment failed');
            }

        } catch (error) {
            console.error('Error during role assignment:', error);

            // Show specific error message
            const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
            const fullError = `Role assignment failed: ${errorMessage}`;

            toast.error(fullError, { autoClose: 7000 });
            setError(fullError);

            // Log additional details for debugging
            console.error('Assignment data that failed:', {
                userCount: selectedUsers.length,
                application: selectedApplication,
                environment: selectedEnvironment,
                roleCount: selectedRoles.length,
                startDate,
                endDate,
                error: error
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle cancel button click
    const handleCancel = () => {
        navigate('/admin-dashboard', { state: { userTypeSelection: selectedUserType, searchState } });
    };

    // Convert applications to dropdown choices
    const applicationChoices = applications.map(app => ({
        value: app.appCd,
        label: app.applicationDesc ? `${app.applicationName} - ${app.applicationDesc}` : app.applicationName
    }));

    // Convert environments to dropdown choices
    const environmentChoices = environments.map(env => ({
        value: env.appEnvCd,
        label: env.envName
    }));

    // Convert roles to dropdown choices
    const roleChoices = roles.map(role => ({
        value: role.roleCd,
        label: role.roleName
    }));

    // Get selected application name for display
    const getSelectedApplicationName = () => {
        const app = applications.find(app => app.appCd === selectedApplication);
        return app ? app.applicationName : '';
    };

    // Get selected environment name for display
    const getSelectedEnvironmentName = () => {
        const env = environments.find(env => env.appEnvCd === selectedEnvironment);
        return env ? env.envName : '';
    };

    // Get selected role names for display
    const getSelectedRoleNames = () => {
        return roles
            .filter(role => selectedRoles.includes(role.roleCd))
            .map(role => role.roleName)
            .join(', ');
    };

    // Update the useEffect for loading applications
    useEffect(() => {
        loadAdminApplications();
    }, [accounts, selectedUserType]);

    const loadAdminApplications = async () => {
        if (accounts.length === 0) {
            return;
        }

        try {
            setLoadingApplications(true);
            setError(null);

            let response;
            let logMessage;

            if (selectedUserType === 'External') {
                // For external users, only show applications that allow external access
                console.log('Loading applications that allow external access');
                logMessage = 'applications that allow external access';
                
                response = await useIdentityAppService().getExternalAccessApplications();
            } else {
                // For internal users, show admin applications (existing behavior)
                const userIdentifier = accounts[0]?.idTokenClaims?.oid;

                if (!userIdentifier) {
                    setError('User identifier not found. Please log in again.');
                    setApplications([]);
                    return;
                }

                console.log('Loading admin applications for user:', userIdentifier);
                logMessage = 'admin applications for user';

                response = await useIdentityAppService().fetchAdminApplications(userIdentifier);
            }

            if (response.ok) {
                const data: ApplicationListResponse = await response.json();

                if (data.success) {
                    setApplications(data.applications);
                    console.log(`Loaded ${data.applications.length} ${logMessage}`);

                    // Log which applications are available
                    if (data.applications.length > 0) {
                        const appNames = data.applications.map(app => app.applicationName);
                        if (selectedUserType === 'External') {
                            console.log('Applications allowing external access:', appNames);
                        } else {
                            console.log('User has admin access to applications:', appNames);
                        }
                    } else {
                        if (selectedUserType === 'External') {
                            console.log('No applications allow external access');
                        } else {
                            console.log('User has no admin access to any applications');
                        }
                    }
                } else {
                    setError(data.message || `Failed to load ${logMessage}`);
                    setApplications([]);
                }
            } else if (response.status === 400) {
                const errorData = await response.json();
                setError(errorData.message || 'Invalid request');
                setApplications([]);
            } else if (response.status === 404) {
                setError(selectedUserType === 'External' ? 'No applications found' : 'User not found in the system');
                setApplications([]);
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.message || 'Failed to load applications');
                setApplications([]);
            }
        } catch (err) {
            console.error('Error loading applications:', err);
            setError(err instanceof Error ? err.message : 'Network error occurred while loading applications');
            setApplications([]);
        } finally {
            setLoadingApplications(false);
        }
    };

    return (
        <React.Fragment>
            <NavBar />

            <div className="subheading grid-row">
                <div className="grid-col flex-1">
                    <h3 className="subheading-title" style={{
                        textAlign: 'center',
                        marginBottom: '20px',
                        marginTop: '40px'
                    }}>
                        Assign Roles & Permissions
                    </h3>
                </div>
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <div style={{
                    padding: '32px',
                    marginBottom: '16px',
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                }}>

                    {/* Loading State */}
                    {loadingApplications && (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            fontSize: '16px',
                            color: '#666'
                        }}>
                            <div>Loading applications...</div>
                        </div>
                    )}

                    {/* No Applications Message */}
                    {!loadingApplications && applications.length === 0 && !error && (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#ff9800',
                            fontSize: '18px',
                            backgroundColor: '#fff3e0',
                            borderRadius: '8px',
                            border: '1px solid #ffb74d'
                        }}>
                            <h4 style={{ marginBottom: '16px', color: '#ff9800' }}>
                                {selectedUserType === 'External' ? 'No Applications Available for External Users' : 'No Admin Applications Found'}
                            </h4>
                            {selectedUserType === 'External' ? (
                                <>
                                    <p>No applications are currently configured to allow external access.</p>
                                    <p style={{ marginTop: '16px', fontSize: '16px' }}>
                                        Only applications marked as "Allow External Access" appear in this list.
                                    </p>
                                    <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                                        Contact your system administrator to enable external access for applications.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p>You don't have administrator access to any applications.</p>
                                    <p style={{ marginTop: '16px', fontSize: '16px' }}>
                                        You can only assign roles for applications where you have admin privileges.
                                    </p>
                                    <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                                        If you believe you should have access, please contact your system administrator.
                                    </p>
                                </>
                            )}
                            <Button
                                onClick={handleCancel}
                            >
                                Return to Dashboard
                            </Button>
                        </div>
                    )}

                    {/* Error Message */}
                    {!loadingApplications && error && (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#d32f2f',
                            fontSize: '18px'
                        }}>
                            <h4 style={{ marginBottom: '16px', color: '#d32f2f' }}>Error Loading Applications</h4>
                            <p>{error}</p>
                            <Button
                                onClick={() => loadAdminApplications()}
                            >
                                Retry
                            </Button>
                            <Button
                                onClick={handleCancel}
                            >
                                Return to Dashboard
                            </Button>
                        </div>
                    )}

                    {/* Normal Form - Only show if user has admin applications */}
                    {!loadingApplications && applications.length > 0 && !error && (
                        <>
                            {/* User Type and Count Display */}
                            <div style={{ marginBottom: '30px' }}>
                                <p style={{
                                    textAlign: 'center',
                                    marginBottom: '10px',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: '#00607f'
                                }}>
                                    User Type: {selectedUserType} | Selected Users: {selectedUsers.length}
                                </p>
                                <p style={{
                                    textAlign: 'center',
                                    marginBottom: '30px',
                                    fontSize: '16px',
                                    color: '#666'
                                }}>
                                    Please select the application, environment, roles, and assignment dates for the selected users.
                                </p>
                            </div>

                            {/* Role Assignment Form */}
                            <div className="grid-row" style={{ marginBottom: '20px' }}>
                                <div className="grid-col flex-1" />
                                <div className="grid-col" style={{
                                    width: "60%",
                                    maxWidth: "800px",
                                    margin: "0 auto"
                                }}>
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <p style={{fontWeight: "bold"}}>Selected User Names: {selectedUsers.map(user => user.userLogin).join(', ')}</p>
                                    </div>

                                    {/* Application Selection - Filtered based on user type */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <Dropdown
                                                choices={applicationChoices}
                                                emptyChoiceLabel="Select Application"
                                                mandatoryLabel={true}
                                                name="application"
                                                label={`Applications`}
                                                value={selectedApplication}
                                                onChange={handleApplicationChange}
                                                disabled={false}
                                            />
                                            {errors.application && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.application}
                                                </div>
                                            )}
                                            <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                                                {selectedUserType === 'External' 
                                                    ? 'Showing only applications that allow external access'
                                                    : 'Showing only applications where you have administrator privileges'
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {/* Environment Selection */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <Dropdown
                                                choices={environmentChoices}
                                                emptyChoiceLabel={
                                                    !selectedApplication
                                                        ? "Select application first"
                                                        : loadingEnvironments
                                                            ? "Loading environments..."
                                                            : environments.length === 0
                                                                ? "No environments available"
                                                                : "Select Environment"
                                                }
                                                mandatoryLabel={true}
                                                name="environment"
                                                label="Environment"
                                                value={selectedEnvironment}
                                                onChange={handleEnvironmentChange}
                                                disabled={!selectedApplication || loadingEnvironments}
                                            />
                                            {errors.environment && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.environment}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Role Selection */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <MultiSelectDropdown
                                                choices={roleChoices}
                                                emptyChoiceLabel={
                                                    !selectedEnvironment
                                                        ? "Select environment first"
                                                        : loadingRoles
                                                            ? "Loading roles..."
                                                            : roles.length === 0
                                                                ? "No roles available"
                                                                : "Select Roles"
                                                }
                                                mandatoryLabel={true}
                                                name="roles"
                                                label="Roles"
                                                value={selectedRoles}
                                                onChange={handleRoleChange}
                                                disabled={!selectedEnvironment || loadingRoles}
                                                searchable={true}
                                                showSelectedAsTags={true}
                                                allowSelectAll={true}
                                            />
                                            {errors.roles && (
                                                <div style={{ color: "red", marginTop: "5px" }}>
                                                    {errors.roles}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Assign Option Selection */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12">
                                            <fieldset style={{ border: 'none', padding: 0 }}>
                                                <legend style={{
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    marginBottom: '10px',
                                                    color: '#1b1b1b'
                                                }}>
                                                    Assignment Option
                                                </legend>
                                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="assignOption"
                                                            value="immediate"
                                                            checked={assignOption === 'immediate'}
                                                            onChange={handleAssignOptionChange}
                                                            style={{
                                                                marginRight: '8px',
                                                                cursor: 'pointer',
                                                                accentColor: '#00607f'
                                                            }}
                                                        />
                                                        <span>Assign Immediately</span>
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="assignOption"
                                                            value="date"
                                                            checked={assignOption === 'date'}
                                                            onChange={handleAssignOptionChange}
                                                            style={{
                                                                marginRight: '8px',
                                                                cursor: 'pointer',
                                                                accentColor: '#00607f'
                                                            }}
                                                        />
                                                        <span>Schedule Assignment</span>
                                                    </label>
                                                </div>
                                            </fieldset>
                                        </div>
                                    </div>

                                    {/* Date Selection Section */}
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12" style={{ paddingRight: '10px' }}>
                                            <InputText
                                                label="Start Date"
                                                name="startDate"
                                                value={startDate}
                                                type="date"
                                                onChange={handleStartDateChange}
                                                mandatoryLabel={true}
                                                readOnly={assignOption === 'immediate' || submitting}
                                                min={getTodayLocalString()}
                                            />
                                            {assignOption === 'immediate' && (
                                                <div style={{ color: "#666", marginTop: "5px", fontSize: "12px", fontStyle: "italic" }}>
                                                    Start date is set to today for immediate assignment
                                                </div>
                                            )}
                                            {errors.startDate && (
                                                <div style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                                                    {errors.startDate}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid-row" style={{ marginBottom: '20px' }}>
                                        <div className="grid-col-12" style={{ paddingRight: '10px' }}>
                                            <InputText
                                                label="End Date"
                                                name="endDate"
                                                value={endDate}
                                                type="date"
                                                onChange={handleEndDateChange}
                                                mandatoryLabel={true}
                                                readOnly={submitting}
                                            />
                                            {errors.endDate && (
                                                <div style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                                                    {errors.endDate}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Notes Field */}
                                    <div className="grid-row" style={{ marginBottom: '30px' }}>
                                        <div className="grid-col-12">
                                            <fieldset
                                                className={"field-wrapper"}
                                                style={{
                                                    padding: "0",
                                                    margin: "0",
                                                    border: "none",
                                                    outline: "none",
                                                    width: "100%"
                                                }}
                                            >
                                                <label htmlFor="assignment-notes-editor" style={{
                                                    display: "block",
                                                    marginBottom: "0.5rem",
                                                    fontSize: "16px",
                                                    fontWeight: "bold",
                                                    color: "#1b1b1b"
                                                }}>
                                                    Notes:
                                                </label>
                                                <ReactQuill
                                                    className="assignment-notes-editor"
                                                    style={{
                                                        border: '1px solid #3e3e3f',
                                                        maxWidth: '30rem',
                                                        width: '100%',
                                                        marginTop: '9px',
                                                        overflowY: 'auto'
                                                    }}
                                                    value={notes}
                                                    onChange={handleNotesChange}
                                                    modules={{
                                                        toolbar: [
                                                            ["bold", "italic", "underline", "strike"],
                                                            [{list: "ordered"}, {list: "bullet"}],
                                                            [{align: []}],
                                                            ["clean"],
                                                        ],
                                                    }}
                                                />
                                            </fieldset>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid-col flex-1" />
                            </div>

                            {/* Action Buttons */}
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "20px",
                                marginTop: "40px"
                            }}>
                                <Button
                                    onClick={handleCancel}
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!selectedApplication || !selectedEnvironment || selectedRoles.length === 0 || !startDate || !endDate || !!errors.startDate || !!errors.endDate || submitting}
                                >
                                    {submitting ? 'Assigning Roles...' : 'Assign Roles'}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <FooterData styles={false} />
        </React.Fragment>
    );
};

export default AssignRoles;